import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import FacebookLogin from '@greatsumini/react-facebook-login';

import {APP_URL, API_URL, LINKEDIN_AUTHORIZATION_URL, TIKTOK_APP_ID} from '../../../settings/local_settings';
import {facebookAppId} from '../../../settings/settings';
import {APIClient} from '../../../utils/APIClient';
import {Spinner} from '../../Spinner';
import {ChannelLogo} from '../../channels/ChannelLogo';
import {ANALYTICS_CHANNEL, FACEBOOK_CHANNEL, GOOGLE_CHANNEL, LINKEDIN_CHANNEL, MERCADO_ADS_CHANNEL, TIKTOK_CHANNEL} from '../../channels/channels';
import {startMercadoAdsAgencyAuth} from '../../channels/mercado_ads/mercadoAdsApi';
import {MercadoAdsErrorView} from '../../channels/mercado_ads/MercadoAdsErrorView';
import {ACTION_SEND_LINK} from '../../channels/mercado_ads/mercadoAds';
import {SendConnectionLinkModal} from '../../connection_links/SendConnectionLinkModal';
import {LoadErrorState} from '../../elements/LoadErrorState';
import {ArrowLeft, ArrowRight, MagnifyingGlass} from '../../icons';
import {clearOauthReturn, setOauthReturn} from '../../onboarding/oauthReturn';
import {clearPendingLinkedinReconnect} from '../../agency/connections/pendingLinkedinReconnect';
import {forgetReconnect} from '../../agency/connections/reconnectNote';
import {useLineWizard, wizardPath} from '../LineWizardScreen';
import {accountKey, effectiveClientId, isMaterialized} from '../lineWizardDraft';
import {
    buildAccountRows, channelName, channelsSyncing, channelsToLoad, exitsAroundRows, mergeRefreshedAccounts,
    platformExits,
} from '../accountsList';
import {materializeLine} from '../materializeLine';
import {materializeErrorMessage} from '../wizardErrors';
import * as api from '../lineWizardApi';

// Con cientos de cuentas (feedback de Pablo del 04/08) dibujar todas traba la pantalla: se muestran
// las primeras y el buscador hace el resto. Las del cliente se muestran siempre enteras.
const MAX_OTHER_ROWS = 150;

// Volver a la pestaña refresca la lista, pero no más de una vez cada tanto: cambiar de pestaña
// dispara `visibilitychange` y `focus` juntos, y cada refresco pide las cuentas de todos los canales.
const REFRESH_MIN_GAP_MS = 5000;

// Mientras una conexión recién hecha sincroniza: los mismos números que Conexiones del clásico
// (`AgencyConnectionsConfigurationContent`, `SYNC_POLL_*`).
export const SYNC_POLL_MS = 8000;
export const SYNC_POLL_MAX_MS = 5 * 60 * 1000;

const STEP_PATH = wizardPath('cuentas');

// El aviso que deja la vuelta de una autorización. GA4 tiene el suyo: lo siguiente es elegir la
// propiedad, no esperar a que aparezcan cuentas.
const NOTICE_KEYS = {
    connected: 'line_wizard.accounts.oauth_connected',
    error: 'line_wizard.accounts.oauth_error',
    ga4_back: 'line_wizard.accounts.ga4_back',
};

/**
 * Paso 3 · Cuentas.
 *
 * El selector del Dashbo clásico, que es el que le gusta a Pablo: la lista completa, ya desplegada,
 * con las cuentas del cliente arriba y un buscador. Sin las tarjetas «VINCULADA» del wizard de JP
 * —esas cuentas no estaban vinculadas a ESTA línea— y sin la etiqueta «del cliente», que Pablo
 * no entendió (04/08): el grupo ya lo dice.
 *
 * Al tocar «Siguiente» se crea la línea (ver `materializeLine`): lo que sigue, el paso 4, trabaja
 * sobre las configuraciones de cuenta que existen recién después de eso.
 */
export function StepAccounts() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {draft, update} = useLineWizard();
    const materialized = isMaterialized(draft);

    const [connections, setConnections] = useState(null);
    const [accountsByChannel, setAccountsByChannel] = useState({});
    const [failedChannels, setFailedChannels] = useState([]);
    const [clientAccounts, setClientAccounts] = useState([]);
    const [loadError, setLoadError] = useState(null);
    const [query, setQuery] = useState('');
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState(null);
    const [oauthNotice, setOauthNotice] = useState(null);
    // Mercado Ads vuelve con su código (`oauth_reason`): el colaborador, sin Product Ads… Se dibuja con su
    // texto y sus acciones en vez del «no se pudo» genérico, que no dice qué hacer.
    const [mercadoAdsError, setMercadoAdsError] = useState(null);
    const [mercadoAdsLinkOpen, setMercadoAdsLinkOpen] = useState(false);
    const [syncTimedOut, setSyncTimedOut] = useState(false);
    const lastLoadAt = useRef(0);
    const creatingRef = useRef(false);
    creatingRef.current = creating;
    // El último pedido lanzado: una respuesta de uno anterior se descarta (review C-rec-fe-linea-09).
    // Con el polling de la sincronización conviven refrescos silenciosos y «Reintentar».
    const loadSeq = useRef(0);
    const connectionsRef = useRef(null);
    connectionsRef.current = connections;

    /**
     * Trae las conexiones, las cuentas del cliente y las de cada canal.
     *
     * `silent` es el refresco al volver a la pestaña o mientras una conexión sincroniza: no pasa por
     * el spinner ni pinta errores. Es un refresco de fondo, así que si algo falla se conserva lo que
     * ya estaba en pantalla (rule de `LoadErrorState`), canal por canal. Se compara con `=== true`
     * porque `load` también va directo a un `onClick` («Reintentar»), que le pasa el evento: un
     * evento no es un refresco silencioso.
     */
    const load = useCallback((mode) => {
        const silent = mode === true;
        const seq = ++loadSeq.current;
        const isLatest = () => seq === loadSeq.current;
        lastLoadAt.current = Date.now();
        if (!silent) {
            setLoadError(null);
            setConnections(null);
        }
        const clientId = effectiveClientId(draft);
        const clientRequest = clientId ? api.fetchClient(clientId) : Promise.resolve(null);

        Promise.all([api.fetchConnections(), clientRequest]).then(([connectionsResponse, clientResponse]) => {
            if (!isLatest()) return;
            if (connectionsResponse?.status !== 'OK' || (clientId && clientResponse?.status !== 'OK')) {
                if (!silent) setLoadError('line_wizard.load_error');
                return;
            }
            const clientData = clientResponse ? clientResponse.data : null;
            setClientAccounts(clientData ? [...(clientData.client_ad_accounts || []), ...(clientData.client_stores || [])] : []);

            const channels = channelsToLoad(connectionsResponse.data);
            Promise.all(channels.map(channel => api.fetchChannelAccounts(channel).then(response => [channel, response])))
                .then(results => {
                    if (!isLatest()) return;
                    const byChannel = {};
                    const failed = [];
                    // Un canal que falla se DICE: nunca se reemplaza por cuentas de ejemplo (el
                    // wizard de JP mostraba cuentas MOCK si fallaba `get-accounts`).
                    results.forEach(([channel, response]) => {
                        if (response?.status !== 'OK') failed.push(channel);
                        else byChannel[channel] = response.data || [];
                    });
                    if (silent) {
                        setAccountsByChannel(previous => mergeRefreshedAccounts(previous, byChannel, failed));
                        setFailedChannels(previous => previous.filter(channel => failed.includes(channel)));
                    } else {
                        setAccountsByChannel(byChannel);
                        setFailedChannels(failed);
                    }
                    setConnections(connectionsResponse.data);
                });
        });
    }, [draft]);

    useEffect(() => {
        // La vuelta del OAuth trae el resultado en la URL: se lee una vez y se limpia.
        const params = new URLSearchParams(window.location.search);
        if (params.get('oauth_error') === MERCADO_ADS_CHANNEL) setMercadoAdsError(params.get('oauth_reason') || 'GENERIC');
        else if (params.get('oauth_error')) setOauthNotice('error');
        else if (params.get('just_connected') === ANALYTICS_CHANNEL) setOauthNotice('ga4_back');
        else if (params.get('just_connected') || params.get('channel')) setOauthNotice('connected');
        if (window.location.search) window.history.replaceState({}, '', window.location.pathname);
        load();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * La lista se actualiza sola al volver a la pestaña.
     *
     * Quien sale a conectar o vincular algo en otra pestaña (Conexiones, la ficha del cliente) volvía
     * a una lista que no se movía, y la cuenta nueva aparecía recién con F5 (recorrido de fede,
     * 01/10, punto 11). Mismo patrón que `QuickConnectAccountModal` (focus) y `PlatformsCard`
     * (visibilitychange). Con la línea ya creada la lista no se usa, y mientras se crea, tampoco.
     */
    useEffect(() => {
        if (materialized) return undefined;
        const onReturn = () => {
            // Sin lista en pantalla (la primera carga en vuelo, o su error) no hay nada que refrescar
            // en silencio: un refresco que la reemplazara y fallara dejaría el spinner para siempre.
            if (document.hidden || creatingRef.current || !connectionsRef.current) return;
            if (Date.now() - lastLoadAt.current < REFRESH_MIN_GAP_MS) return;
            load(true);
        };
        document.addEventListener('visibilitychange', onReturn);
        window.addEventListener('focus', onReturn);
        return () => {
            document.removeEventListener('visibilitychange', onReturn);
            window.removeEventListener('focus', onReturn);
        };
    }, [load, materialized]);

    /**
     * Mientras una conexión recién hecha sincroniza, la lista se vuelve a pedir sola.
     *
     * Al volver del OAuth las cuentas todavía no existen (ver `channelsSyncing`): sin esto, el grupo
     * quedaba vacío hasta un F5 (review C-rec-fe-linea-01). Cada 8 s y hasta 5 minutos, como
     * Conexiones del clásico; al vencer, «Actualizar la lista» queda a mano.
     */
    const syncingChannels = useMemo(() => channelsSyncing(connections), [connections]);
    const syncing = !materialized && syncingChannels.length > 0;
    const loadRef = useRef(load);
    loadRef.current = load;
    useEffect(() => {
        if (!syncing) return undefined;
        const startedAt = Date.now();
        setSyncTimedOut(false);
        const timer = setInterval(() => {
            if (Date.now() - startedAt >= SYNC_POLL_MAX_MS) {
                clearInterval(timer);
                setSyncTimedOut(true);
                return;
            }
            if (!creatingRef.current) loadRef.current(true);
        }, SYNC_POLL_MS);
        return () => clearInterval(timer);
    }, [syncing]);

    const rows = useMemo(
        () => buildAccountRows({accountsByChannel, clientAccounts, query}),
        [accountsByChannel, clientAccounts, query],
    );
    const selectedKeys = useMemo(() => new Set(draft.accounts.map(accountKey)), [draft.accounts]);

    const toggle = row => {
        if (materialized) return;
        const key = accountKey(row);
        update(current => ({
            ...current,
            accounts: selectedKeys.has(key)
                ? current.accounts.filter(account => accountKey(account) !== key)
                : [...current.accounts, {channel: row.channel, account_id: row.account_id, name: row.name}],
        }));
    };

    const next = async () => {
        if (materialized) {
            navigate(wizardPath('campanas'));
            return;
        }
        if (draft.accounts.length === 0 || creating) return;
        setCreating(true);
        setCreateError(null);
        const result = await materializeLine({draft, update, api});
        setCreating(false);
        if (!result.ok) {
            setCreateError(materializeErrorMessage(t, result));
            return;
        }
        navigate(wizardPath('campanas'));
    };

    const connect = channel => {
        setOauthNotice(null);
        setMercadoAdsError(null);
        // La nota se escribe antes de elegir la rama, así que la rama que no sale del paso la borra
        // (el `else` del final): una nota armada para un canal sin flujo propio desviaría la próxima
        // conexión hecha desde otro lado.
        setOauthReturn(STEP_PATH);
        // Una conexión nueva: el «Reconectar» de una cuenta que se abandonó no puede colarse acá.
        forgetReconnect();
        // Google y GA4 autorizan por la misma puerta (`get_auth_url/…`) y sus callbacks respetan la
        // nota de vuelta. GA4 abría Conexiones en otra pestaña y el usuario quedaba ahí, con un
        // «exitoso» y el wizard sin moverse (punto 11): ahora vuelve a este paso, con su aviso.
        if (channel === GOOGLE_CHANNEL || channel === ANALYTICS_CHANNEL) {
            api.fetchAuthUrlFor(channel === ANALYTICS_CHANNEL ? 'ga4/' : '').then(data => {
                if (data?.auth_url) {
                    window.location = data.auth_url;
                    return;
                }
                clearOauthReturn();
                setOauthNotice('error');
            });
            return;
        }
        if (channel === TIKTOK_CHANNEL) {
            const redirect = encodeURIComponent(`${APP_URL}connect-tiktok`);
            window.location.href = `https://business-api.tiktok.com/portal/auth?app_id=${TIKTOK_APP_ID}` +
                `&state=onboarding&redirect_uri=${redirect}`;
            return;
        }
        if (channel === LINKEDIN_CHANNEL) {
            clearPendingLinkedinReconnect();
            window.location.href = LINKEDIN_AUTHORIZATION_URL;
            return;
        }
        // Mercado Ads pide la URL con el `state` de la agencia y su callback respeta la nota de vuelta. Si
        // no se puede ir, el paso avisa como con Google.
        if (channel === MERCADO_ADS_CHANNEL) {
            startMercadoAdsAgencyAuth().then(result => {
                if (result.ok) return;
                clearOauthReturn();
                setOauthNotice('error');
            });
            return;
        }
        // Meta autoriza en su popup (`PlatformExit`) y no pasa por acá. Un canal nuevo sin rama
        // propia no sale del paso: no deja la nota armada.
        clearOauthReturn();
    };

    // El aviso: el del OAuth, o el de una sincronización en curso aunque se haya llegado de otro
    // lado. «Ya puedes elegir la propiedad» recién cuando las propiedades de GA4 llegaron: mientras
    // sincroniza, el de «puede tardar unos minutos».
    let notice = oauthNotice || (syncing ? 'connected' : null);
    if (notice === 'ga4_back' && syncingChannels.includes(ANALYTICS_CHANNEL)) notice = 'connected';

    if (loadError !== null) {
        return (
            <div className="lw-step">
                <LoadErrorState title={t(loadError)} text={t('line_wizard.load_error_explanation')}
                                retryLabel={t('line_wizard.load_error_retry')} onRetry={load}/>
            </div>
        );
    }
    if (connections === null) return <div className="box-loader"><Spinner color="positive"/></div>;

    const shownOthers = rows.others.slice(0, MAX_OTHER_ROWS);
    const agencyList = exitsAroundRows(shownOthers, materialized ? [] : platformExits(connections),
                                       {filtered: query.trim().length > 0});

    return (
        <div className="lw-step">
            <div className="ob-head">
                <h1>{t('line_wizard.accounts.title')}</h1>
                <p>{t('line_wizard.accounts.subtitle')}</p>
            </div>

            {materialized && <p className="ob-explain">{t('line_wizard.accounts.already_created')}</p>}
            {mercadoAdsError && (
                <div className="lw-mercado-ads-error">
                    <MercadoAdsErrorView code={mercadoAdsError}
                                         onAction={(action) => (action === ACTION_SEND_LINK
                                             ? setMercadoAdsLinkOpen(true)
                                             : connect(MERCADO_ADS_CHANNEL))}/>
                </div>
            )}
            <SendConnectionLinkModal show={mercadoAdsLinkOpen} onHide={() => setMercadoAdsLinkOpen(false)}
                                     channel={MERCADO_ADS_CHANNEL} agencyClientId={effectiveClientId(draft) || undefined}/>
            {notice && (
                <p className={`ob-explain${notice === 'error' ? ' lw-error' : ''}`}>
                    {t(NOTICE_KEYS[notice])}
                    {/* Pasados los 5 minutos el polling se detiene: la salida queda a mano. */}
                    {syncing && syncTimedOut && (
                        <button type="button" className="ob-link lw-inline-link" onClick={() => load(true)}>
                            {t('line_wizard.accounts.refresh')}
                        </button>
                    )}
                </p>
            )}

            <label className="ob-search lw-search">
                <MagnifyingGlass size={16} className="ob-search__icon"/>
                <input className="ob-search__input" type="search" value={query}
                       aria-label={t('line_wizard.accounts.search_placeholder')}
                       placeholder={t('line_wizard.accounts.search_placeholder')}
                       onChange={event => setQuery(event.target.value)}/>
            </label>

            {failedChannels.map(channel => (
                <p key={channel} className="lw-error lw-failed" role="alert">
                    {t('line_wizard.accounts.channel_failed', {channel: channelName(channel),
                                                               interpolation: {escapeValue: false}})}
                    <button type="button" className="ob-link lw-inline-link" onClick={() => load()}>
                        {t('line_wizard.load_error_retry')}
                    </button>
                </p>
            ))}

            <div className="lw-accounts">
                {rows.client.length > 0 && (
                    <section>
                        <h2 className="lw-accounts__group">{t('line_wizard.accounts.group_client')}</h2>
                        <div className="lw-list">
                            {rows.client.map(row => (
                                <AccountRow key={accountKey(row)} row={row} checked={selectedKeys.has(accountKey(row))}
                                            disabled={materialized} onToggle={toggle}/>
                            ))}
                        </div>
                    </section>
                )}

                <section>
                    <h2 className="lw-accounts__group">
                        {t('line_wizard.accounts.group_all')}
                        <span className="lw-accounts__count">
                            {/* Buscando, lo útil es cuántas coinciden, no «usa el buscador». */}
                            {query.trim()
                                ? t('line_wizard.accounts.group_all_matches', {count: rows.others.length})
                                : t('line_wizard.accounts.group_all_count', {count: rows.totalOthers})}
                        </span>
                    </h2>
                    <div className="lw-list">
                        {agencyList.map(item => (item.exit ? (
                            <PlatformExit key={`exit-${item.exit.channel}`} exit={item.exit} onConnect={connect}
                                          onCancelled={() => setOauthNotice('error')}/>
                        ) : (
                            <AccountRow key={accountKey(item.row)} row={item.row}
                                        checked={selectedKeys.has(accountKey(item.row))}
                                        disabled={materialized} onToggle={toggle}/>
                        )))}
                    </div>
                    {rows.others.length > MAX_OTHER_ROWS && (
                        <p className="lw-help lw-accounts__more">
                            {t('line_wizard.accounts.showing_some', {shown: MAX_OTHER_ROWS, count: rows.others.length})}
                        </p>
                    )}
                    {rows.client.length === 0 && rows.others.length === 0 && (
                        <p className="lw-help lw-accounts__more">
                            {query ? t('line_wizard.accounts.no_matches') : t('line_wizard.accounts.none')}
                        </p>
                    )}
                </section>
            </div>

            {createError && <p className="lw-error" role="alert">{createError}</p>}

            <div className="lw-foot -sticky">
                <span className="lw-foot__note">
                    {t('line_wizard.accounts.selected_count', {count: draft.accounts.length})}
                </span>
                <span className="lw-foot__spacer"/>
                <button type="button" className="ob-btn -ghost" onClick={() => navigate(wizardPath('linea'))}
                        disabled={creating}>
                    <ArrowLeft size={16}/> {t('line_wizard.back')}
                </button>
                <button type="button" className="ob-btn -primary" onClick={next}
                        disabled={creating || (!materialized && draft.accounts.length === 0)}>
                    {creating
                        ? <><Spinner color="negative small"/> {t('line_wizard.accounts.creating')}</>
                        : <>{createError ? t('line_wizard.retry') : t('line_wizard.next')} <ArrowRight size={16}/></>}
                </button>
            </div>
        </div>
    );
}

function AccountRow({row, checked, disabled, onToggle}) {
    return (
        <label className={`lw-account${checked ? ' -on' : ''}`}>
            <input type="checkbox" checked={checked} disabled={disabled} onChange={() => onToggle(row)}/>
            <ChannelMark channel={row.channel}/>
            <span className="lw-account__body">
                <span className="lw-account__name">{row.name}</span>
                <span className="lw-account__meta">{channelName(row.channel)} · ID {row.account_id}</span>
            </span>
        </label>
    );
}

/** El logo del canal en una caja fija de 20px (ver `.lw-logo`: DV360 no es un `<img>`). */
function ChannelMark({channel}) {
    return (
        <span className="lw-logo">
            <ChannelLogo channel={channel} hspace={0} vspace={0} marginLeft={0} padding={0} width={20} height={20}/>
        </span>
    );
}

/**
 * «Agregar otro acceso a X (otro correo)» o «Conectar una cuenta de X».
 *
 * Todas autorizan desde el propio paso: Google, GA4, TikTok y LinkedIn navegando y volviendo con la
 * nota de `oauthReturn`; Meta en un popup. GA4 abría Conexiones en otra pestaña hasta el 02/10
 * (punto 11): su callback no conocía el wizard.
 *
 * Meta nunca sale del paso, así que su destino se decide acá. OJO: `<FacebookLogin>` DESCARTA la
 * prop `onClick` —desestructura una lista fija—, por eso no se intenta dejar ninguna nota de retorno
 * desde ahí (card dJxYEHZ9).
 */
function PlatformExit({exit, onConnect, onCancelled}) {
    const {t} = useTranslation();
    const label = exit.connected
        ? t('line_wizard.accounts.link_more', {channel: channelName(exit.channel), interpolation: {escapeValue: false}})
        : t('line_wizard.accounts.connect_new', {channel: channelName(exit.channel), interpolation: {escapeValue: false}});

    if (exit.channel === FACEBOOK_CHANNEL) {
        return (
            <FacebookLogin
                appId={facebookAppId}
                scope="ads_management,ads_read,business_management"
                loginOptions={{auth_type: 'reauthenticate'}}
                onSuccess={response => {
                    new APIClient(API_URL + 'store_facebook_access_token/')
                        .call('POST', {...response, credential_label: null})
                        .then(data => {
                            clearOauthReturn();
                            window.location = data?.status === 'OK'
                                ? `${STEP_PATH}?just_connected=${FACEBOOK_CHANNEL}`
                                : `${STEP_PATH}?oauth_error=${FACEBOOK_CHANNEL}`;
                        });
                }}
                onFail={() => {
                    clearOauthReturn();
                    onCancelled();
                }}
                className="lw-exit"
            >
                <ChannelMark channel={exit.channel}/>
                <span>{label}</span>
            </FacebookLogin>
        );
    }

    return (
        <button type="button" className="lw-exit" onClick={() => onConnect(exit.channel)}>
            <ChannelMark channel={exit.channel}/>
            <span>{label}</span>
        </button>
    );
}
